/**
 * Issue #179 — 知识库条目管理 API 客户端。
 *
 * 所有读写都走真实后端 `/admin/knowledge*`（ADMIN+ / orgId 隔离 / AuditLog）。
 * 红线：禁止前端假状态；列只能来自后端 `/admin/knowledge/columns` 白名单；
 * pageSize 上限由后端兜底（100）。
 */
import { apiClient } from './api';
import type { DeleteCaptchaProof } from './captcha-api';
import type {
  CreateKnowledgeRequest,
  KnowledgeCategory,
  KnowledgeColumnsResponse,
  KnowledgeEntry,
  KnowledgeListQuery,
  KnowledgeListResponse,
  UpdateKnowledgeRequest,
} from './types';

/** 列白名单：列隐藏 / 显示面板只能从这里取列，不得前端硬编码数据库字段。 */
export function listKnowledgeColumnsApi(): Promise<KnowledgeColumnsResponse> {
  return apiClient.get<KnowledgeColumnsResponse>('/admin/knowledge/columns');
}

/** 本租户知识库分类（筛选下拉）。 */
export function listKnowledgeCategoriesApi(): Promise<KnowledgeCategory[]> {
  return apiClient.get<KnowledgeCategory[]>('/admin/knowledge/categories');
}

/** 高级检索 + 分页 + 列选择。 */
export function listKnowledgeEntriesApi(
  query: KnowledgeListQuery = {},
): Promise<KnowledgeListResponse> {
  return apiClient.get<KnowledgeListResponse>(
    '/admin/knowledge',
    query as unknown as Record<string, unknown>,
  );
}

/** 条目详情（长答案完整可读）。 */
export function getKnowledgeEntryApi(id: string): Promise<KnowledgeEntry> {
  return apiClient.get<KnowledgeEntry>(`/admin/knowledge/${encodeURIComponent(id)}`);
}

export function createKnowledgeEntryApi(
  body: CreateKnowledgeRequest,
): Promise<KnowledgeEntry> {
  return apiClient.post<KnowledgeEntry>('/admin/knowledge', body);
}

export function updateKnowledgeEntryApi(
  id: string,
  body: UpdateKnowledgeRequest,
): Promise<KnowledgeEntry> {
  return apiClient.patch<KnowledgeEntry>(
    `/admin/knowledge/${encodeURIComponent(id)}`,
    body,
  );
}

/** 软删除（后端写 deletedAt + isActive=false，绝不物理删除）。Issue #235：需图形验证码。 */
export function softDeleteKnowledgeEntryApi(
  id: string,
  proof: DeleteCaptchaProof,
): Promise<{ id: string; deleted: boolean }> {
  return apiClient.delete<{ id: string; deleted: boolean }>(
    `/admin/knowledge/${encodeURIComponent(id)}`,
    proof,
  );
}
